import type { AsyncSequence } from "../types/AsyncSequence"

import { Void } from "../functions/Void"
import { SafeIterable } from "./SafeIterable"

/**
 * Processes an async iterable, calling callbacks for each yielded value and on completion.
 * Errors are caught and passed to the on_stop callback.
 *
 * @param iterable - The async sequence to process
 * @param on_yield - Callback invoked for each yielded value (default: no-op)
 * @param on_stop - Callback invoked when iteration completes or errors (default: throws on error)
 * @returns A promise that resolves when processing is complete
 *
 * @example
 * ```ts
 * import { ProcessIterable } from "@pdfking/tfunc"
 *
 * async function* numbers() {
 *   yield 1;
 *   yield 2;
 *   yield 3;
 * }
 *
 * await ProcessIterable(
 *   numbers(),
 *   (value, index) => console.log(`Item ${index}: ${value}`),
 *   (error) => {
 *     if (error) console.error('Error:', error);
 *     else console.log('Done');
 *   }
 * );
 * ```
 */
export const ProcessIterable = async <T>(
  iterable: AsyncSequence<T>,
  on_yield: (data: T, index: number) => void = Void,
  on_stop: (error: Error | undefined) => void = e => {
    if (e) throw e
  }
): Promise<void> => {
  let index = 0
  const { error, stream } = SafeIterable(iterable)
  for await (const data of stream) {
    void on_yield(data, index++)
  }
  return on_stop(await error)
}
